import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { translate } from '@utils/i18n';
import { saveCacheToLocalStorage } from '../../../../helpers/localStorage';

const namespace = 'features.dashboards.home.inspection_assistant_widget';
const IA_INSIGHTS_CACHE_KEY = 'kendo/ia_insights_v2';

const { $ } = window;

function formatDate(date) {
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const y = date.getFullYear();
  return `${m}/${d}/${y}`;
}

function setLast30DaysFilter() {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 30);
  saveCacheToLocalStorage(IA_INSIGHTS_CACHE_KEY, {
    values: { dateRange: { rangeType: 'custom_range', startDate: formatDate(start), endDate: formatDate(end) } },
    active: { dateRange: true }
  });
}

function InspectionAssistantWidget({ inspectionCount, escaped, resolved, totalIssues, expandLink }) {
  const handleNavigate = () => {
    setLast30DaysFilter();
    window.location.href = expandLink;
  };

  const escapedChartRef = useRef();
  const resolvedChartRef = useRef();
  const totalChartRef = useRef();
  const widgetRef = useRef(null);
  const containerRef = useRef(null);

  const updateSparkline = () => {
    const baseWidget = containerRef.current?.firstElementChild;
    if (!baseWidget) return;

    const widgetPadding = 28;
    const areaWidth = baseWidget.clientWidth - widgetPadding * 2;

    const escapedSparkline = $(escapedChartRef.current).data('kendoSparkline');
    escapedSparkline?.setOptions({ chartArea: { width: areaWidth } });

    const resolvedSparkline = $(resolvedChartRef.current).data('kendoSparkline');
    resolvedSparkline?.setOptions({ chartArea: { width: areaWidth / 2 } });

    const totalSparkline = $(totalChartRef.current).data('kendoSparkline');
    totalSparkline?.setOptions({ chartArea: { width: areaWidth / 2 } });
  };

  useEffect(() => {
    const baseWidget = containerRef.current?.parentNode?.firstElementChild;

    const resizeObserver = new ResizeObserver(() => {
      updateSparkline();
    });

    if (baseWidget) {
      resizeObserver.observe(baseWidget);
    }

    return () => resizeObserver.disconnect();
  }, [containerRef]);

  const drawSparkline = () => {
    const isMobile = 'ontouchstart' in window;
    const widgetStyle = window.getComputedStyle(widgetRef.current);
    const widgetPadding = parseInt(widgetStyle.getPropertyValue('padding-right'), 10);
    const areaWidth = widgetRef.current.clientWidth - widgetPadding * 2;

    $(escapedChartRef.current).kendoSparkline({
      type: 'area',
      chartArea: { width: areaWidth, height: 30 },
      seriesDefaults: { color: '#F2E3E7', opacity: 1 },
      tooltip: { color: 'white', visible: !isMobile },
      data: escaped.histogram
    });

    $(resolvedChartRef.current).kendoSparkline({
      type: 'area',
      chartArea: { width: areaWidth / 2, height: 30 },
      seriesDefaults: { color: '#E1F7EE', opacity: 1 },
      tooltip: { color: 'white', visible: !isMobile },
      data: resolved.histogram
    });

    $(totalChartRef.current).kendoSparkline({
      type: 'area',
      chartArea: { width: areaWidth / 2, height: 30 },
      seriesDefaults: { color: '#D9E5FC', opacity: 1 },
      tooltip: { color: 'white', visible: !isMobile },
      data: totalIssues.histogram
    });
  };

  useEffect(() => {
    drawSparkline();
  }, []);

  return (
    <div className="qmb-widget--deficiencies--ia-assistant--sm" ref={containerRef}>
      <div className="widget__header">
        <svg className="widget__accent" role="img" x="0" y="0" viewBox="0 0 64 64">
          <polygon points="64,0 0,64 0,0 " />
        </svg>
        <h3 className="widget__title">
          <a
            href={expandLink}
            className="title__link"
            title={translate('view_all', { namespace })}
            onClick={setLast30DaysFilter}>
            {translate('title', { namespace })}
          </a>
          <small>{translate('subtitle', { namespace, inspectionCount })}</small>
        </h3>
        <div className="widget__controls">
          <a
            href={expandLink}
            className="qmb-control--icon"
            title={translate('expand_title', { namespace })}
            aria-label={translate('expand_title', { namespace })}
            onClick={setLast30DaysFilter}>
            <i className="fa-light fa-chart-mixed" />
          </a>
        </div>
      </div>

      <div className="widget__body" ref={widgetRef}>
        <div className="widget__cell widget__cell--x-full valueContainer">
          <button type="button" className="widget__counter" onClick={handleNavigate}>
            <span className="counter__label">{translate('escaped', { namespace })}</span>
            <span className="counter__value">{escaped.count}</span>
          </button>
          <div ref={escapedChartRef} />
        </div>

        <div className="widget__cell valueContainer">
          <button type="button" className="widget__counter" onClick={handleNavigate}>
            <span className="counter__label">{translate('resolved', { namespace })}</span>
            <span className="counter__value">{resolved.count}</span>
          </button>
          <div ref={resolvedChartRef} />
        </div>

        <div className="widget__cell valueContainer">
          <button type="button" className="widget__counter" onClick={handleNavigate}>
            <span className="counter__label">{translate('total_issues_caught', { namespace })}</span>
            <span className="counter__value">{totalIssues.count}</span>
          </button>
          <div ref={totalChartRef} />
        </div>
      </div>
    </div>
  );
}

InspectionAssistantWidget.defaultProps = {
  inspectionCount: 0,
  expandLink: '',
  escaped: { count: 0, histogram: [0] },
  resolved: { count: 0, histogram: [0] },
  totalIssues: { count: 0, histogram: [0] }
};

InspectionAssistantWidget.propTypes = {
  inspectionCount: PropTypes.number,
  expandLink: PropTypes.string,
  escaped: PropTypes.shape({
    count: PropTypes.number,
    histogram: PropTypes.arrayOf(PropTypes.number)
  }),
  resolved: PropTypes.shape({
    count: PropTypes.number,
    histogram: PropTypes.arrayOf(PropTypes.number)
  }),
  totalIssues: PropTypes.shape({
    count: PropTypes.number,
    histogram: PropTypes.arrayOf(PropTypes.number)
  })
};

export default InspectionAssistantWidget;
